<template>
  <div
    class="cpt-chat-slider pointer"
    :class="{ 
      disabled: disabled, 
      [`cpt-chat-slider-${direction}`]: true,
      'click-trans': clickChanging,
    }"
    :style="basicStyle"
    ref="slider"
    @click="handleClickChange"
  >
    <div
      class="cpt-chat-slider-rail"
      :style="transitionStyle"
    >
      <div
        class="cpt-chat-slider-marks"
        v-if="validMarks"
      >
        <div
          class="cpt-chat-slider-mark-item"
          :class="{ active: item.value === dotValue }"
          :style="item.style"
          v-for="item in validMarks"
          :key="shouldBeObjItem ? item.value[dataValue] : item.value"
        >
          <div class="cpt-chat-slider-mark-item-dot" v-if="!hideMarkDot" />
          <div
            class="cpt-chat-slider-mark-item-value"
            v-if="!hideLabel"
            v-html="
              markLabel(shouldBeObjItem ? item.value[dataValue] : item.value)
            "
          />
        </div>
      </div>
      <div
        class="cpt-chat-slider-process"
        :style="[processStyle]"
      />
      <div
        class="cpt-chat-slider-dot"
        :style="[dotStyle]"
        @mousedown.stop="startDrag"
        @mouseenter="dotHover = true"
        @mouseleave="dotHover = false"
        @focus="dotFocus = true"
        @blur="dotFocus = false"
        ref="dot"
      >
        <div class="cpt-chat-slider-dot-inner"></div>
        <div
          class="cpt-chat-slider-tooltip"
          :class="[...tooltipClass, {
            'show': clickChanging || dotHover || dotFocus,
          }]"
          ref="tooltip"
          v-if="tooltip"
          :style="tooltipPos"
        >
          <div
            class="cpt-chat-slider-tooltip-inner"
            v-html="markLabel(dotValue, true)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
const HORIZON_DIRECTION = ["ltr", "rtl"];
const VERTICAL_DIRECTION = ["ttb", "btt"];

export default {
  name: "Slider",
  props: {
    value: {
      // 组件绑定的值
      type: Number,
      default: 0,
    },
    width: {
      // 宽度
      type: Number,
      default: undefined,
      validator: (val) => val > 0,
    },
    height: {
      // 高度
      type: Number,
      default: 4,
      validator: (val) => val > 0,
    },
    dotSize: {
      // 小球
      type: Number,
      default: 14,
    },
    min: {
      // 最小值
      type: Number,
      default: undefined,
    },
    max: {
      // 最大值
      type: Number,
      default: undefined,
    },
    interval: {
      // 步长
      type: Number,
      default: 1,
      validator: (val) => val > 0,
    },
    disabled: {
      // 是否禁用
      type: Boolean,
      default: false,
    },
    tooltip: {
      // 是否显示tooltip
      type: Boolean,
      default: true,
    },
    tooltipAppendBody: {
      // 是否显示tooltip到body上
      type: Boolean,
      default: false,
    },
    tooltipFormatter: {
      // tooltip的格式化函数
      type: Function,
      default: null,
    },
    tooltipPlacement: {
      // tooltip的位置
      type: String,
      default: "",
    },
    duration: {
      // transition duration
      type: Number,
      default: 0.16,
      validator: (val) => val >= 0,
    },
    marks: {
      // 标记点
      type: Array,
      default: null,
    },
    lazy: {
      // 懒更新 默认使用 否则每一次拖拽都会触发更新事件，导致CPU占用比较高
      type: Boolean,
      default: true,
    },
    included: {
      // 需要传入对应的mark, 否则不生效
      type: Boolean,
      default: false,
    },
    data: {
      // data 值，用于获取对应的label
      type: Array,
      default: null,
    },
    dataValue: {
      // data 数据的值
      type: String,
      default: "value",
    },
    dataLabel: {
      // data 数据的label
      type: String,
      default: "label",
    },
    adsorb: {
      // 吸附，自动吸附到当前的mark上对应的有效值上
      type: Boolean,
      default: false,
    },
    hideLabel: {
      // 是否隐藏label
      type: Boolean,
      default: false,
    },
    direction: {
      // 滑动方向
      type: String,
      default: "ltr", // ltr rtl btt ttb
      validator: (val) => {
        return [...HORIZON_DIRECTION, ...VERTICAL_DIRECTION].includes(val);
      }
    },
    hideMarkDot: {
      // 是否显示mark的小圆点
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isMoving: false,
      dotValue: 0, // 组件内部控制小球的值

      clickChanging: false, // 点击改变时过度的状态标记
      CONSTANT_ADSORB: 0.01, // 吸附的阈值

      tooltipPos: {},
      dotHover: false,
      dotFocus: false,
    };
  },
  created() {
    this.updateDotVal(this.value);
  },
  mounted() {
    window.addEventListener("blur", this.blurListener);
    if (this.tooltipBodyType) {
      document.body.appendChild(this.$refs['tooltip']);
    }
  },
  beforeDestroy() {
    this.unbind();
    window.removeEventListener("blur", this.blurListener);
    window.removeEventListener("mousedown", this.listenMouseDown) 
    if (this.tooltipBodyType) {
      this.$refs['tooltip'].remove();
    }
  },
  computed: {
    calProcess() {
      if (this.dotValue < this.realMin || this.dotValue > this.realMax)
        return 0;
      return (
        ((this.dotValue - this.realMin) / (this.realMax - this.realMin)) * 100
      );
    },
    basicStyle() {
      if (HORIZON_DIRECTION.includes(this.direction)) {
        return {
          width: !this.width ? "auto" : `${this.width}px`,
          padding: `${this.dotSize / 2}px`,
        };
      }
      return {
        height: !this.height ? "auto" : `${this.height}px`,
        padding: `${this.dotSize / 2}px`,
      };
    },
    processStyle() {
      switch (this.direction) {
        case "ltr":
          return {
            width: this.calProcess + "%",
          };
        case "btt":
          return {
            height: this.calProcess + "%",
          };
      }
    },
    dotStyle() {
      switch (this.direction) {
        case "ltr":
          return {
            width: `${this.dotSize}px`,
            height: `${this.dotSize}px`,
            top: "50%",
            left: `${this.calProcess}%`,
            transform: `translate(-50%, -50%)`,
            borderRadius: `${this.dotSize / 2}px`,
          };
        case "btt":
          return {
            width: `${this.dotSize}px`,
            height: `${this.dotSize}px`,
            left: "50%",
            bottom: `${this.calProcess}%`,
            transform: `translate(-50%, 50%)`,
            borderRadius: `${this.dotSize / 2}px`,
          };
      }
    },
    transitionStyle() {
      return {
        transitionDuration: this.isMoving ? "0s" : `${this.duration}s`,
      };
    },
    tooltipClass() {
      return [
        `cpt-chat-slider-tooltip-${
          this.tooltipPlacement || this.defaultPlacement
        }`,
      ];
    },
    isMarkItemObj() {
      return this.marks && this.marks[0] && typeof this.marks[0] === "object";
    },
    isDataItemObj() {
      return this.data && this.data[0] && typeof this.data[0] === "object";
    },
    shouldBeObjItem() {
      return !!this.isMarkItemObj || !!this.isDataItemObj;
    },
    validMarks() {
      const result = []
      if (this.marks){
        const validatedMarks = this.validateListProps(this.marks);
        result.push(
          ...(validatedMarks.length > 0
            ? this.unifiedItem(validatedMarks, this.isMarkItemObj)
          : [])
        )
      }
      if(this.validData){
        result.push(
          ...(this.validData.length > 0 
            ? this.unifiedItem(this.validData, this.isDataItemObj)
          : [])
        )
      }
      return result.length > 0 ? result : null;
    },
    validData() {
      const validatedData = this.validateListProps(this.data);
      return validatedData.length > 0 ? validatedData : null;
    },
    adsorbDistance() {
      return this.CONSTANT_ADSORB * (this.realMax - this.realMin);
    },
    sortedData() {
      if (!this.data) return [];
      return this.data.sort((a, b) => a[this.dataValue] - b[this.dataValue]);
    },
    realMin() {
      if (this.sortedData.length > 0) {
        return this.sortedData[0][this.dataValue];
      }

      if (this.min !== undefined) return this.min;

      return 0;
    },
    realMax() {
      if (this.sortedData.length > 0) {
        return this.sortedData[this.sortedData.length - 1][this.dataValue];
      }

      if (this.max !== undefined) return this.max;

      return 100;
    },
    fractionDigits() {
      if (this.interval < 1 && this.interval > 0) {
        return this.interval.toString().split(".")[1].length;
      }
      return 0;
    },
    defaultPlacement() {
      return HORIZON_DIRECTION.includes(this.direction) ? "top" : "left";
    },
    tooltipBodyType() {
      return this.tooltipAppendBody && this.tooltip
    }
  },
  watch: {
    value(newVal) {
      this.updateDotVal(newVal);

      if (this.tooltipBodyType) {
        this.updateTooltipPos();
        this.startTooltipAnimation()
      }
    },
    clickChanging(val,oldV) {
      if(oldV !== undefined) {
        if (val) {
          window.addEventListener("mousedown", this.listenMouseDown)
        } else {
          window.removeEventListener("mousedown", this.listenMouseDown)
        }
      }
    },
  },
  methods: {
    startDrag(_e) {
      if (this.disabled) return;
      window.addEventListener("mousemove", this.dragFunc);
      window.addEventListener("mouseup", this.stopDrag);
      this.isMoving = true;
    },
    dragFunc(e) {
      const newVal = this.getVal(e);
      if (this.lazy) {
        this.dotValue = newVal;
      } else {
        this.handleChange(newVal);
      }
      if (this.tooltipBodyType) {
        this.updateTooltipPos();
      }
    },
    stopDrag(_e) {
      this.unbind();
      this.isMoving = false;
      if (this.lazy) {
        this.handleChange(this.dotValue);
      }
    },
    handleClickChange(e) {
      if (this.disabled) return;

      this.clickChanging = true;
      const newVal = this.getVal(e);
      this.handleChange(newVal);

      if (this.tooltipBodyType) {
        this.startTooltipAnimation()
      }
    },
    handleChange(newVal) {
      if (this.disabled) return;

      // Included 情况下，计算当前值最近那个值
      if (this.included) {
        newVal = this.includedModeGetVal(newVal);
      }

      // adsorb 模式下，吸附到最近的值
      if (this.adsorb && this.validMarks) {
        const tempVal = this.validMarks.reduce((prev, curr) => {
          //  是否在吸附范围内
          const inAdsorb = Math.abs(curr.value - newVal) < this.adsorbDistance;
          if (inAdsorb) {
            return Math.abs(curr.value - newVal) < Math.abs(prev.value - newVal)
              ? curr
              : prev;
          } else {
            return prev;
          }
        }, Infinity);
        //  如果吸附值有效
        if (tempVal !== Infinity) {
          newVal = tempVal[this.dataValue];
        }
      }

      this.dotValue = newVal;
      this.noticeParent(newVal);
    },
    // 根据当前的鼠标位置计算值
    getVal(e) {
      if (this.disabled) return;

      const actualPercent = this.getPercent(e);
      const originVal =
        (this.realMax - this.realMin) * actualPercent + this.realMin;
      const newVal = Math.round(originVal / this.interval) * this.interval;
      if (this.fractionDigits) {
        return +newVal.toFixed(this.fractionDigits);
      }
      return newVal;
    },
    unbind() {
      window.removeEventListener("mousemove", this.dragFunc);
      window.removeEventListener("mouseup", this.stopDrag);
    },
    blurListener() {
      if (this.isMoving) {
        this.stopDrag();
      }
    },
    noticeParent(newVal) {
      this.$emit("input", newVal);
      this.$emit("change", newVal);
    },
    getMarkStyle(val) {
      let percent;
      switch (this.direction) {
        case "ltr":
          percent =
            ((val - this.realMin) / (this.realMax - this.realMin)) * 100;
          return {
            left: `${percent}%`,
          };
        case "btt":
          percent =
            ((val - this.realMin) / (this.realMax - this.realMin)) * 100;
          return {
            bottom: `${percent}%`,
          };
      }
    },
    markLabel(v, isTooltip = false) {
      if (this.validData) {
        const item = this.validData.find((item) => item[this.dataValue] == v);
        if (item) {
          v = item[this.dataLabel];
        }
      }
      if (isTooltip && this.tooltipFormatter) {
        v = this.tooltipFormatter(v);
      }
      return v;
    },
    validateListProps(prop) {
      if (!Array.isArray(prop)) return [];
      const unique = [...new Set(prop)];
      const validatedProp = unique.filter((m) => {
        if (m instanceof Object) {
          const v = m[this.dataValue];
          return this.checkValid(v);
        } else {
          return this.checkValid(m);
        }
      });
      return validatedProp;
    },
    checkValid(m) {
      if (m < this.realMin || m > this.realMax) return false;
      if (m % this.interval !== 0) return false;
      return true;
    },
    includedModeGetVal(newVal) {
      if (!this.validMarks) {
        console.warn("没有有效的marks值");
        return newVal;
      }
      const result = this.validMarks.reduce((prev, curr) => {
        return Math.abs(curr[this.dataValue] - newVal) <
          Math.abs(prev[this.dataValue] - newVal)
          ? curr
          : prev;
      }, this.validMarks[0])[this.dataValue];
      return result;
    },
    updateDotVal(newVal) {
      let val = newVal;
      if (this.included) {
        val = this.includedModeGetVal(val);
      }
      this.dotValue = val;
    },
    getPercent(e) {
      const { width, height, left, right, top, bottom } =
        this.$el?.getBoundingClientRect();
      let originPercent;
      switch (this.direction) {
        case "ltr":
          originPercent = (e.clientX - left) / width;
          return Math.min(1, Math.max(0, originPercent));
        case "btt":
          originPercent = (bottom - e.clientY) / height;
          return Math.min(1, Math.max(0, originPercent));
      }
    },
    listenMouseDown(_e){
      const preventDom = this.$el
      if(!preventDom.contains(_e.target)){
        this.clickChanging = false
      }
    },
    unifiedItem(list, isObj) {
      return list.map((value) => {
        if(this.shouldBeObjItem){
          const objVal = isObj ? 
            value : 
            {
              label: value,
              value
            }
          return {
            value,
            style: this.getMarkStyle(objVal[this.dataValue]),
          };
        }
        return {
          value,
          style: this.getMarkStyle(value),
        };
      })
    },
    updateTooltipPos() {
      this.$nextTick(() => {
        let dot = this.$refs['dot'], tooltip = this.$refs['tooltip']
        let rect = dot.getBoundingClientRect(), tooltip_rect = tooltip.getBoundingClientRect()
        let left = rect.left - (tooltip_rect.width / 2) + (rect.width / 2)
        this.tooltipPos = {
          left: left + 'px',
          top: rect.top - 10 - tooltip_rect.height + 'px',
          transform: 'unset',
        }
        if (left + tooltip_rect.width > window.innerWidth) {
          this.tooltipPos.left = 'unset'
          this.tooltipPos.right = 0
        }
      })
    },
    startTooltipAnimation() {
      if (!this.isMoving && this.duration) {
        if (!this._dotMoving) {
          this._dotMoving = true
          window.requestAnimationFrame(this.toolTopPosAnimation);
        }
        clearTimeout(this.dotMovingTimer)
        this.dotMovingTimer = setTimeout(() => {
          this._dotMoving = false
        }, this.duration * 1000 + 100)
      }
    },
    toolTopPosAnimation() {
      if (this._dotMoving && !this._isDestroyed) {
        this.updateTooltipPos()
        window.requestAnimationFrame(this.toolTopPosAnimation);
      }
    }
  },
};
</script>

<style lang="scss">
.cpt-chat-slider {
  position: relative;
  box-sizing: content-box;
  user-select: none;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
    .cpt-chat-slider-dot {
      position: absolute;
      cursor: not-allowed !important;

      .cpt-chat-slider-dot-inner {
        background: #ccc;
        &:active {
          border: 2px solid $ct-wh-0 !important;
        }
      }
    }
  }

  &-ltr {
    .cpt-chat-slider-rail {
      position: relative;
      width: auto;
      height: 4px;
      background: $d-ln-1;
      &::before,
      &::after {
        content: "";
        position: absolute;
        width: 7px;
        top: 0;
        bottom: 0;
        z-index: 1;
      }
      &::before {
        left: -7px;
        background: $d-bd-tx;
        border-radius: 15px 0px 0px 15px;
      }
      &::after {
        right: -7px;
        background: $d-ln-1;
        border-radius: 0px 15px 15px 0px;
      }
      .cpt-chat-slider-marks {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: transparent;
        .cpt-chat-slider-mark-item {
          position: absolute;
          top: 50%;
          height: 12px;
          transform: translate(-50%, -50%);
          color: $d-tx-3;
          &.active {
            color: $d-bd-tx;
            .cpt-chat-slider-mark-item-dot {
              background: $d-bd-tx;
            }
          }
          .cpt-chat-slider-mark-item-dot {
            width: 2px;
            height: 12px;
            background-color: $d-ln-1;
            border-radius: 0;
            display: block !important;
          }
          .cpt-chat-slider-mark-item-value {
            position: absolute;
            bottom: -2px;
            left: 50%;
            transform: translate(-50%, 100%);
            color: inherit;
            text-align: center;
            font-size: 12px;
            font-weight: 400;
            line-height: 20px;
            letter-spacing: 0.48px;
            white-space: nowrap;
          }
        }
      }
      .cpt-chat-slider-process {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        background: $d-bd-tx;
        transition: width cubic-bezier(0, 0, 0.58, 1);
        transition-duration: inherit;
      }
      .cpt-chat-slider-dot {
        position: absolute;
        background: $ct-wh-0;
        z-index: 2;
        transition: all cubic-bezier(0, 0, 0.58, 1);
        transition-duration: inherit;
        cursor: pointer;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35));

        .cpt-chat-slider-dot-inner {
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          border-radius: 50%;
          border: 2px solid $ct-wh-0;
          transition: left cubic-bezier(0, 0, 0.58, 1);
          transition-duration: inherit;

          &:active {
            border: 2px solid $d-bd-tx;
          }
        }
        &:focus,
        &:hover,
        &:active {
          .cpt-chat-slider-tooltip {
            visibility: visible;
          }
        }

        .cpt-chat-slider-tooltip {
          position: absolute;
          visibility: hidden;
          &::after {
            content: "";
            position: absolute;
            width: 0;
            height: 0;
          }
          &-top {
            top: -10px;
            left: 50%;
            transform: translate(-50%, -100%);
            &::after {
              left: 50%;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-top: 5px solid $d-fl-ip;
              transform: translateX(-50%);
            }
          }
          &-bottom {
            bottom: -10px;
            left: 50%;
            transform: translate(-50%, 100%);
            &::after {
              left: 50%;
              border-left: 5px solid transparent;
              border-right: 5px solid transparent;
              border-bottom: 5px solid $d-fl-ip;
              top: -5px;
              transform: translateX(-50%);
            }
          }
          &-left {
            left: -10px;
            top: 50%;
            transform: translate(-100%, -50%);
            &::after {
              top: 50%;
              transform: translateY(-50%);
              border-top: 5px solid transparent;
              border-bottom: 5px solid transparent;
              border-left: 5px solid $d-fl-ip;
              right: -5px;
            }
          }
          &-right {
            right: -10px;
            top: 50%;
            transform: translate(100%, -50%);
            &::after {
              top: 50%;
              transform: translateY(-50%);
              border-top: 5px solid transparent;
              border-bottom: 5px solid transparent;
              border-right: 5px solid $d-fl-ip;
              left: -5px;
            }
          }
          .cpt-chat-slider-tooltip-inner {
            min-width: 20px;
            padding: 2px 5px;
            box-sizing: content-box;
            background: $d-fl-ip;
            border-radius: 5px;

            color: $d-tx-1;
            font-size: 14px;
            text-align: center;
            white-space: nowrap;
          }
        }
      }
    }
  }
  &-btt {
    .cpt-chat-slider-rail {
      position: relative;
      height: 100%;
      width: 4px;
      background: $d-ln-1;
      &::before,
      &::after {
        content: "";
        position: absolute;
        height: 7px;
        width: 4px;
        left: 0;
        right: 0;
      }
      &::before {
        top: -7px;
        background: $d-ln-1;
        border-radius: 15px 15px 0px 0px;
      }
      &::after {
        bottom: -7px;
        background: $d-bd-tx;
        border-radius: 0px 0px 15px 15px;
      }
      .cpt-chat-slider-marks {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background: transparent;
        .cpt-chat-slider-mark-item {
          position: absolute;
          top: 50%;
          width: 12px;
          transform: translate(-50%, -50%);
          color: $d-tx-3;
          &.active {
            color: $d-bd-tx;
            .cpt-chat-slider-mark-item-dot {
              background: $d-bd-tx;
            }
          }
          .cpt-chat-slider-mark-item-dot {
            height: 2px;
            width: 12px;
            background-color: $d-ln-1;
            border-radius: 0;
            display: block !important;
          }
          .cpt-chat-slider-mark-item-value {
            position: absolute;
            bottom: -2px;
            left: 50%;
            transform: translate(-50%, 100%);
            color: inherit;
            text-align: center;
            font-size: 12px;
            font-weight: 400;
            line-height: 20px;
            letter-spacing: 0.48px;
            white-space: nowrap;
          }
        }
      }
      .cpt-chat-slider-process {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        background: $d-bd-tx;
        transition: height cubic-bezier(0, 0, 0.58, 1);
        transition-duration: inherit;
      }
      .cpt-chat-slider-dot {
        position: absolute;
        background: $ct-wh-0;
        z-index: 2;
        transition: all cubic-bezier(0, 0, 0.58, 1);
        transition-duration: inherit;
        cursor: pointer;

        .cpt-chat-slider-dot-inner {
          width: 100%;
          height: 100%;
          box-sizing: border-box;
          border-radius: 50%;
          border: 2px solid $ct-wh-0;
          transition: all cubic-bezier(0, 0, 0.58, 1);
          transition-duration: inherit;

          &:active {
            border: 2px solid $d-bd-tx;
          }
        }
        &:focus,
        &:hover,
        &:active {
          .cpt-chat-slider-tooltip {
            visibility: visible;
          }
        }
      }
    }
  }

  &.click-trans {
    .cpt-chat-slider-dot-inner {
      border: 2px solid $d-bd-tx !important;
    }
  }
}
.cpt-chat-slider-tooltip {
  position: absolute;
  visibility: hidden;
  z-index: 10000;
  pointer-events: none;
  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
  }
  &-top {
    top: -10px;
    left: 50%;
    transform: translate(-50%, -100%);
    &::after {
      left: 50%;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid $d-fl-ip;
      transform: translateX(-50%);
    }
  }
  &-bottom {
    bottom: -10px;
    left: 50%;
    transform: translate(-50%, 100%);
    &::after {
      left: 50%;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 5px solid $d-fl-ip;
      top: -5px;
      transform: translateX(-50%);
    }
  }
  &-left {
    left: -10px;
    top: 50%;
    transform: translate(-100%, -50%);
    &::after {
      top: 50%;
      transform: translateY(-50%);
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      border-left: 5px solid $d-fl-ip;
      right: -5px;
    }
  }
  &-right {
    right: -10px;
    top: 50%;
    transform: translate(100%, -50%);
    &::after {
      top: 50%;
      transform: translateY(-50%);
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      border-right: 5px solid $d-fl-ip;
      left: -5px;
    }
  }
  .cpt-chat-slider-tooltip-inner {
    min-width: 20px;
    padding: 2px 5px;
    box-sizing: content-box;
    background: $d-fl-ip;
    border-radius: 5px;

    color: $d-tx-1;
    font-size: 14px;
    text-align: center;
    white-space: nowrap;
  }
  &.show {
    visibility: visible !important;
  }
}
</style>
